import type { ApolloError } from '@apollo/client';
import { createForm, type RenderParams } from '@seek/forms-ui';
import { required, rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Box,
  Button,
  ButtonLink,
  Card,
  Heading,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';

import appTranslations from 'src/App/App.vocab';
import TrackingLink from 'src/App/Components/TrackingLink/TrackingLink';
import { CLICK_ADD_USER_STEP1_CANCEL } from 'src/constants/tracking';
import { BASE_URL } from 'src/constants/urls';
import { useUserList } from 'src/hooks/use-user-list';
import { useConfig } from 'src/modules/ConfigProvider';
import { isValidSsoEmail } from 'src/utils/customValidationHelpers';
import { userExistsOnAccount } from 'src/utils/validators/userExistsOnAccount/userExistsOnAccount';

import translations from './.vocab';

interface FormValues {
  email: string;
}
type FormProps = RenderParams<FormValues>;

const { FormProvider, useField } = createForm<FormValues>();

type VerifyEmailFormProps = FormProps & VerifyEmailProps;

const VerifyEmailForm = ({
  handleSubmit,
  setEmail,
  error,
}: VerifyEmailFormProps) => {
  const { urlResolver } = useConfig();
  const { data: users } = useUserList();
  const { t } = useTranslations(translations);
  const { t: appTrans } = useTranslations(appTranslations);

  const emailValidationRules = rules.hirerEmail({
    requiredLabel: t('Email address required label'),
  });

  const email = useField({
    id: 'email',
    validators: [
      required,
      isValidSsoEmail,
      userExistsOnAccount,
      ...emailValidationRules.validators,
    ],
    validatorProps: {
      ...emailValidationRules.validatorProps,
      users: (users?.usersList?.users || []).map((u) => ({ email: u.email })),
    },
  });

  // @ts-ignore:next-line
  const onSubmit = handleSubmit(async (formValues) => {
    setEmail(formValues.email.toLocaleLowerCase());
  });

  const id = users?.usersList?.users?.find(
    (user) => user.email.toLowerCase() === email.value.toLowerCase(),
  )?.id;

  return (
    <form onSubmit={onSubmit}>
      <Stack space="medium">
        <Stack space="large">
          <Heading level="3">{appTrans('Individual details')}</Heading>
          <Card>
            <TextField
              id="email"
              label={appTrans('Email')}
              value={email.value}
              message={<ErrorMessage message={email.errorMessage} id={id} />}
              tone={email.errorMessage ? 'critical' : undefined}
              reserveMessageSpace
              onChange={email.onChange}
              onBlur={email.onBlur}
            />
          </Card>
        </Stack>
        {error && (
          <Alert tone="critical">
            <Text>{t('Verify Email Error')}</Text>
          </Alert>
        )}

        <Actions>
          <Button type="submit" tone="formAccent">
            {t('Next')}
          </Button>
          <TrackingLink
            renderAs={ButtonLink}
            variant="transparent"
            href={urlResolver({ path: `/${BASE_URL}` })}
            data-analytics={CLICK_ADD_USER_STEP1_CANCEL}
          >
            {appTrans('Cancel button')}
          </TrackingLink>
        </Actions>
      </Stack>
    </form>
  );
};

export interface VerifyEmailProps {
  setEmail: (email: string) => void;
  error?: ApolloError;
}

export const VerifyEmail = ({ setEmail, error }: VerifyEmailProps) => {
  const { locale } = useConfig();
  return (
    <FormProvider initialValues={{ email: '' }} language={locale}>
      {(formProps) => (
        <VerifyEmailForm setEmail={setEmail} error={error} {...formProps} />
      )}
    </FormProvider>
  );
};

const ErrorMessage = ({ message, id }: { message: string; id?: string }) => {
  const { t } = useTranslations(translations);

  if (message === 'userExists') {
    return (
      <>
        <Box>
          {t('Email been invited to account message')}&nbsp;
          <TextLink href={`edit/${id}`}>{t('View team member')}</TextLink>.
        </Box>
      </>
    );
  }
  return <>{message}</>;
};
